import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import html2pdf from 'html2pdf.js';
import { 
  Bone,
  Search,
  AlertTriangle,
  Download,
  RotateCcw,
  Sparkles,
  Loader2,
  CheckCircle2,
  User,
  Stethoscope
} from 'lucide-react';

import { API_URL } from '../config';

export default function TraumatoUrgencesPage() {
  const { token } = useAuth();
  const printRef = useRef(null);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  const [formData, setFormData] = useState(() => {
    try {
      const saved = localStorage.getItem('traumato-urgences');
      return saved ? JSON.parse(saved) : {
        regionAnatomique: '',
        typeTraumatisme: '',
        patientAge: '',
        fractureOuverte: null,
        deplacement: null,
        etatCutane: '',
        examenNeurologique: '',
        examenVasculaire: '',
        complications: []
      };
    } catch {
      return {
        regionAnatomique: '',
        typeTraumatisme: '',
        patientAge: '',
        fractureOuverte: null,
        deplacement: null,
        etatCutane: '',
        examenNeurologique: '',
        examenVasculaire: '',
        complications: []
      };
    }
  });

  useEffect(() => {
    localStorage.setItem('traumato-urgences', JSON.stringify(formData));
  }, [formData]);

  const handleReset = () => {
    if (window.confirm('Réinitialiser le formulaire ?')) {
      localStorage.removeItem('traumato-urgences');
      window.location.reload();
    }
  };

  const handleExportPDF = async () => {
    try {
      toast.info('Génération du PDF...');
      const opt = {
        margin: 10,
        filename: `Traumato-Urgences-${new Date().toISOString().split('T')[0]}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2 },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
      };
      await html2pdf().set(opt).from(printRef.current).save();
      toast.success('PDF exporté');
    } catch (error) {
      toast.error('Erreur export PDF');
    }
  };

  const handleAnalyzeAI = async () => {
    try {
      setIsAnalyzing(true);
      toast.info('Analyse IA en cours...');

      const response = await fetch(`${API_URL}/api/ai/analyze-traumato`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) throw new Error('Erreur analyse');
      
      const data = await response.json();
      setAiAnalysis(data);
      toast.success('Analyse terminée');
    } catch (error) {
      toast.error('Erreur analyse IA');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const regionsAnatomiques = [
    { value: 'epaule', label: 'Épaule (Clavicule, Humérus proximal, Luxations)' },
    { value: 'coude', label: 'Coude (Humérus distal, Radius, Ulna)' },
    { value: 'poignet_main', label: 'Poignet/Main (Radius distal, Carpe, Métacarpes, Phalanges)' },
    { value: 'bassin', label: 'Bassin (Branches ilio-ischio, Cotyle)' },
    { value: 'femur', label: 'Fémur (Col, Per-trochantérienne, Diaphyse)' },
    { value: 'genou', label: 'Genou (Fémur distal, Rotule, Plateau tibial, Ligaments)' },
    { value: 'jambe', label: 'Jambe (Diaphyse tibiale/péronière)' },
    { value: 'cheville', label: 'Cheville (Bi-malléolaire, Pilon, Tendon Achille)' },
    { value: 'pied', label: 'Pied (Tarse, Métatarses, Phalanges, Entorses)' }
  ];

  const classificationsImportantes = [
    { nom: 'CAUCHOIX (Fractures ouvertes)', types: ['Type I: Plaie ponctiforme', 'Type II: Plaie contuse', 'Type III: Perte substance'] },
    { nom: 'GARDEN (Col fémur)', types: ['I: Valga', 'II: Sans déplacement', 'III: Vara', 'IV: Tête libre'] }
  ];

  return (
    <div className="p-3 md:p-6 space-y-3 md:space-y-4 max-w-5xl mx-auto">
      {/* Header */}
      <div className="space-y-2 print:hidden">
        <div className="flex flex-col sm:flex-row justify-between gap-2">
          <div className="flex items-center gap-2">
            <Bone className="w-6 h-6 md:w-8 md:h-8 text-orange-400" />
            <div>
              <h1 className="text-lg md:text-2xl font-bold text-[hsl(var(--text))]">Traumato aux Urgences</h1>
              <p className="text-xs text-[hsl(var(--text-dim))]">Prise en charge orthopédique</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleAnalyzeAI} disabled={isAnalyzing || !formData.regionAnatomique} size="sm" className="bg-purple-400/20 text-purple-300 border-purple-400/30">
              {isAnalyzing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            </Button>
            <Button onClick={handleReset} size="sm" variant="outline" className="text-amber-300 border-amber-400/30">
              <RotateCcw className="w-4 h-4" />
            </Button>
            <Button onClick={handleExportPDF} size="sm" className="bg-cyan-400/20 text-cyan-300 border-cyan-400/30">
              <Download className="w-4 h-4" />
            </Button>
          </div>
        </div>
        <Alert className="bg-orange-400/10 border-orange-400/30 p-2">
          <Bone className="h-4 w-4 text-orange-400" />
          <AlertDescription className="text-xs text-[hsl(var(--text))]">
            <strong>Guide traumato :</strong> 9 régions anatomiques, protocoles complets
          </AlertDescription>
        </Alert>
      </div>

      <div ref={printRef} className="space-y-3">
        {/* SÉLECTION RÉGION */}
        <Card className="border-cyan-400/30 bg-cyan-400/5">
          <CardHeader className="p-3">
            <CardTitle className="text-sm md:text-base text-cyan-300">RÉGION ANATOMIQUE</CardTitle>
          </CardHeader>
          <CardContent className="p-3">
            <Select value={formData.regionAnatomique} onValueChange={(val) => setFormData(prev => ({ ...prev, regionAnatomique: val }))}>
              <SelectTrigger className="bg-[hsl(var(--bg-elev))]">
                <SelectValue placeholder="Sélectionner une région..." />
              </SelectTrigger>
              <SelectContent>
                {regionsAnatomiques.map((region) => (
                  <SelectItem key={region.value} value={region.value}>{region.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        {/* ÉVALUATION PATIENT */}
        <Card className="border-purple-400/30">
          <CardHeader className="p-3">
            <CardTitle className="text-sm md:text-base text-purple-300 flex items-center gap-2">
              <User className="w-4 h-4" />
              ÉVALUATION PATIENT
            </CardTitle>
          </CardHeader>
          <CardContent className="p-3 space-y-3">
            <div>
              <label className="text-xs font-medium text-[hsl(var(--text))] mb-2 block">Âge patient</label>
              <div className="flex gap-2">
                {['< 60 ans', '≥ 60 ans'].map((age) => (
                  <button
                    key={age}
                    onClick={() => setFormData(prev => ({ ...prev, patientAge: age }))}
                    className={`flex-1 px-3 py-2 text-xs rounded-lg transition-all ${
                      formData.patientAge === age
                        ? 'bg-purple-500 text-white'
                        : 'bg-[hsl(var(--bg-elev))] text-[hsl(var(--text-dim))]'
                    }`}
                  >
                    {age}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-xs font-medium text-[hsl(var(--text))] mb-2 block">Fracture ouverte</label>
              <div className="flex gap-2">
                {['Non', 'Type I', 'Type II', 'Type III'].map((type) => (
                  <button
                    key={type}
                    onClick={() => setFormData(prev => ({ ...prev, fractureOuverte: type }))}
                    className={`flex-1 px-2 py-1.5 text-xs rounded-lg transition-all ${
                      formData.fractureOuverte === type
                        ? type === 'Non' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
                        : 'bg-[hsl(var(--bg-elev))] text-[hsl(var(--text-dim))]'
                    }`}
                  >
                    {type}
                  </button>
                ))}
              </div>
              {formData.fractureOuverte && formData.fractureOuverte !== 'Non' && (
                <p className="text-[10px] text-red-300 mt-1">Classification CAUCHOIX</p>
              )}
            </div>

            <div>
              <label className="text-xs font-medium text-[hsl(var(--text))] mb-2 block">Déplacement</label>
              <div className="flex gap-2">
                {['Non déplacée', 'Peu déplacée', 'Déplacée'].map((dep) => (
                  <button
                    key={dep}
                    onClick={() => setFormData(prev => ({ ...prev, deplacement: dep }))}
                    className={`flex-1 px-2 py-1.5 text-xs rounded-lg transition-all ${
                      formData.deplacement === dep
                        ? dep === 'Non déplacée' ? 'bg-green-500 text-white' : dep === 'Peu déplacée' ? 'bg-amber-500 text-white' : 'bg-red-500 text-white'
                        : 'bg-[hsl(var(--bg-elev))] text-[hsl(var(--text-dim))]'
                    }`}
                  >
                    {dep}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-xs font-medium text-[hsl(var(--text))] mb-1.5 block">État cutané</label>
              <Input
                value={formData.etatCutane}
                onChange={(e) => setFormData(prev => ({ ...prev, etatCutane: e.target.value }))}
                placeholder="Normal, érythème, menace cutanée..."
                className="h-9 text-sm"
              />
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs font-medium text-[hsl(var(--text))] mb-1.5 block">Examen neurologique</label>
                <Input
                  value={formData.examenNeurologique}
                  onChange={(e) => setFormData(prev => ({ ...prev, examenNeurologique: e.target.value }))}
                  placeholder="Normal, déficit..."
                  className="h-9 text-sm"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-[hsl(var(--text))] mb-1.5 block">Examen vasculaire</label>
                <Input
                  value={formData.examenVasculaire}
                  onChange={(e) => setFormData(prev => ({ ...prev, examenVasculaire: e.target.value }))}
                  placeholder="Pouls +, pouls -..."
                  className="h-9 text-sm"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* CLASSIFICATIONS IMPORTANTES */}
        <Card className="border-amber-400/30 bg-amber-400/5">
          <CardHeader className="p-3">
            <CardTitle className="text-sm md:text-base text-amber-300">CLASSIFICATIONS IMPORTANTES</CardTitle>
          </CardHeader>
          <CardContent className="p-3">
            <Accordion type="single" collapsible>
              {classificationsImportantes.map((classif, idx) => (
                <AccordionItem key={idx} value={`classif-${idx}`}>
                  <AccordionTrigger className="text-xs md:text-sm">{classif.nom}</AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-1">
                      {classif.types.map((type, i) => (
                        <div key={i} className="text-xs text-[hsl(var(--text))] flex items-start gap-2">
                          <CheckCircle2 className="w-3 h-3 text-amber-400 mt-0.5" />
                          <span>{type}</span>
                        </div>
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </CardContent>
        </Card>

        {/* PROTOCOLES PAR RÉGION */}
        {formData.regionAnatomique && (
          <Card className="border-green-400/30 bg-green-400/5">
            <CardHeader className="p-3">
              <CardTitle className="text-sm md:text-base text-green-300 flex items-center gap-2">
                <Stethoscope className="w-4 h-4" />
                PROTOCOLE DE PRISE EN CHARGE
              </CardTitle>
            </CardHeader>
            <CardContent className="p-3">
              <Alert className="bg-blue-500/10 border-blue-400/30 p-2 mb-3">
                <AlertDescription className="text-xs text-blue-300">
                  <strong>Région sélectionnée :</strong> {regionsAnatomiques.find(r => r.value === formData.regionAnatomique)?.label}
                </AlertDescription>
              </Alert>
              
              <div className="space-y-2 text-xs">
                <div className="p-2 rounded bg-[hsl(var(--bg-elev))]">
                  <div className="font-semibold text-green-300 mb-1">Radiologie</div>
                  <div className="text-[hsl(var(--text-dim))]">Rx face + profil, Scanner si fracture complexe</div>
                </div>
                
                <div className="p-2 rounded bg-[hsl(var(--bg-elev))]">
                  <div className="font-semibold text-green-300 mb-1">Examen clinique à réaliser</div>
                  <div className="space-y-0.5 text-[hsl(var(--text-dim))]">
                    <div>• Recherche lésion nerveuse</div>
                    <div>• Recherche lésion vasculaire (pouls)</div>
                    <div>• État cutané</div>
                    <div>• Recherche luxation associée</div>
                  </div>
                </div>

                <div className="p-2 rounded bg-[hsl(var(--bg-elev))]">
                  <div className="font-semibold text-green-300 mb-1">Prévention systématique</div>
                  <div className="text-[hsl(var(--text-dim))]">• HBPM (fractures membres inférieurs)</div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* CRITÈRES DE DÉCISION */}
        {formData.fractureOuverte && formData.fractureOuverte !== 'Non' && (
          <Alert className="bg-red-500/20 border-red-500/40 p-3">
            <AlertTriangle className="h-4 w-4 text-red-400" />
            <AlertDescription className="text-xs text-red-300">
              <strong>FRACTURE OUVERTE {formData.fractureOuverte}</strong><br/>
              • Type II-III : Appel chirurgien URGENT<br/>
              • ATB si nécessaire<br/>
              • Conditionnement pour bloc opératoire
            </AlertDescription>
          </Alert>
        )}

        {formData.deplacement === 'Déplacée' && (
          <Alert className="bg-amber-500/20 border-amber-500/40 p-3">
            <AlertTriangle className="h-4 w-4 text-amber-400" />
            <AlertDescription className="text-xs text-amber-300">
              <strong>FRACTURE DÉPLACÉE</strong><br/>
              • Avis chirurgical recommandé<br/>
              • Immobilisation d'attente<br/>
              • Réévaluation nécessaire
            </AlertDescription>
          </Alert>
        )}

        {/* AI ANALYSIS */}
        {aiAnalysis && (
          <Card className="border-purple-500/30 bg-purple-500/5">
            <CardHeader className="p-3">
              <CardTitle className="text-sm md:text-base text-purple-300 flex items-center gap-2">
                <Sparkles className="w-4 h-4" />
                ANALYSE IA - Recommandations Traumato
              </CardTitle>
            </CardHeader>
            <CardContent className="p-3">
              <div className="prose prose-sm max-w-none">
                <div className="whitespace-pre-wrap text-xs md:text-sm text-[hsl(var(--text))]">
                  {aiAnalysis.analysis}
                </div>
              </div>
              <Alert className="mt-3 bg-amber-400/10 border-amber-400/30 p-2">
                <AlertDescription className="text-xs text-amber-300">
                  <strong>Disclaimer :</strong> Aide à la décision, avis orthopédique requis
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
